import { create } from 'zustand';

import { useAccountStore } from './use-account-store';
import { getAllSymbols } from './use-markets-store';

interface FavoritesStore {
  favorites: Set<string>;
  clearFavorites: () => void;
  initFavorites: () => void;
  saveFavorites: () => void;
  getIsFavorite: (symbol: string) => boolean;
  toggleFavorite: (symbol: string) => void;
  addFavorites: (symbols: string[]) => void;
  removeFavorites: (symbols: string[]) => void;
  listAll: () => void;
}

const useFavoritesStore = create<FavoritesStore>((set, get) => ({
  favorites: new Set(),
  clearFavorites() {
    set({ favorites: new Set([]) });
    get().saveFavorites();
  },
  initFavorites() {
    const { address } = useAccountStore.getState();
    const stored = localStorage.getItem(
      address ? `favoriteMarkets-${address}` : 'favoriteMarkets',
    );
    set({ favorites: new Set(stored ? JSON.parse(stored) : []) });
  },
  getIsFavorite: (symbol: string) => get().favorites.has(symbol),
  toggleFavorite: (symbol: string) => {
    let newFavorites: Set<string> = new Set();
    set((state) => {
      newFavorites = new Set(state.favorites);
      if (newFavorites.has(symbol)) {
        newFavorites.delete(symbol);
      } else {
        newFavorites.add(symbol);
      }
      return { favorites: newFavorites };
    });
    get().saveFavorites();
  },
  addFavorites: (symbols) => {
    set((state) => {
      const newFavorites = new Set(state.favorites);
      symbols.forEach((symbol) => newFavorites.add(symbol));
      return { favorites: newFavorites };
    });
    get().saveFavorites();
  },
  removeFavorites: (symbols) => {
    set((state) => {
      const newFavorites = new Set(state.favorites);
      symbols.forEach((symbol) => newFavorites.delete(symbol));
      return { favorites: newFavorites };
    });
    get().saveFavorites();
  },
  saveFavorites() {
    const { favorites } = get();
    const { address } = useAccountStore.getState();
    localStorage.setItem(
      address ? `favoriteMarkets-${address}` : 'favoriteMarkets',
      JSON.stringify([...favorites]),
    );
  },
  listAll() {
    set({ favorites: new Set(getAllSymbols()) });
    get().saveFavorites();
  },
}));

export default useFavoritesStore;
